import React, { ReactNode } from 'react';
import './StatusDisplay.scss';
import { FilterKAI } from 'components/Icons';

type StatusType = 'loading' | 'error' | 'empty' | 'warning';

interface StatusDisplayProps {
  type: StatusType;
  title?: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: ReactNode;
  className?: string;
}

/**
 * A reusable component for displaying various status states like loading, error, empty, etc.
 * It provides a consistent UI for these states across the application.
 */
const StatusDisplay: React.FC<StatusDisplayProps> = ({
  type,
  title,
  message,
  actionLabel,
  onAction,
  icon,
  className = '',
}) => {
  // Default titles based on type
  const getDefaultTitle = () => {
    switch (type) {
      case 'loading':
        return 'Loading...';
      case 'error':
        return 'An error occurred';
      case 'empty':
        return 'No data available';
      case 'warning':
        return 'Warning';
      default:
        return '';
    }
  };

  // Default icons based on type
  const getDefaultIcon = () => {
    if (icon) return icon;

    switch (type) {
      case 'loading':
        return <div className="status-display__loading-spinner"></div>;
      case 'error':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M15 9L9 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'empty':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M20 6L9 17L4 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`status-display status-display--${type} ${className}`}>
      <div className="status-display__content">
        <div className="status-display__icon">
          <FilterKAI />
        </div>
        <div className="status-display__title">{title || getDefaultTitle()}</div>
        {message && <div className="status-display__message">{message}</div>}
        {getDefaultIcon()}
        {actionLabel && onAction && (
          <button className="status-display__action-button" onClick={onAction}>
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default StatusDisplay;
